/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, Eye, X, Layers, Users, BookOpen, CheckSquare, Square, UserPlus, CheckCircle2, AlertCircle, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface ClassType {
  id: string;
  name: string;
  teacherId: string | null;
  teacher: {
    name: string;
    email: string;
  } | null;
}

interface TeacherType {
  id: string;
  name: string;
  email: string;
}

interface GlobalClassType {
  id: string;
  name: string;
  createdAt?: string;
}

const AllClasses: React.FC = () => {
  const [classes, setClasses] = useState<ClassType[]>([]);
  const [teachers, setTeachers] = useState<TeacherType[]>([]);
  const [globalClasses, setGlobalClasses] = useState<GlobalClassType[]>([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [globalMessage, setGlobalMessage] = useState("");
  const [showGlobalModal, setShowGlobalModal] = useState(false);
  const [selectedGlobalClassIds, setSelectedGlobalClassIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [teachersError, setTeachersError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassType | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    teacherId: "",
  });

  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
// Student Assignment Modal State
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [selectedClassForStudents, setSelectedClassForStudents] = useState<ClassType | null>(null);
  const [allStudents, setAllStudents] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [classStudents, setClassStudents] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [studentSearch, setStudentSearch] = useState("");
  const [isBulkAssigningStudents, setIsBulkAssigningStudents] = useState(false);
  const [studentMessage, setStudentMessage] = useState("");
  const [studentError, setStudentError] = useState("");
// Subject Assignment Modal State
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [selectedClassForSubjects, setSelectedClassForSubjects] = useState<ClassType | null>(null);
  const [allSubjects, setAllSubjects] = useState<Array<{ id: string; name: string }>>([]);
  const [classSubjects, setClassSubjects] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<Set<string>>(new Set());
  const [subjectSearch, setSubjectSearch] = useState("");
  const [isBulkAssigningSubjects, setIsBulkAssigningSubjects] = useState(false);
  const [subjectMessage, setSubjectMessage] = useState("");
  const [subjectError, setSubjectError] = useState("");

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canDeleteClass = role !== "headteacher";
  const canImportGlobal = role === "admin" || role === "superadmin";
  const canCreateClass = role === "superadmin";
  const canManageClasses = role === "admin" || role === "superadmin";

  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(getApiUrl("academics/classes"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data.classes || [];
      setClasses(data);
    } catch (err) {
      setError("Failed to load classes. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTeachers = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("users/teachers"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data.teachers || [];
      setTeachers(data);
    } catch (err) {
      setTeachersError("Teachers list is unavailable right now. You can still manage classes.");
    }
  }, [token]);

  const fetchGlobalClasses = useCallback(async () => {
    if (!canImportGlobal) return;
    try {
      setGlobalLoading(true);
      setGlobalError("");
      const res = await axios.get(getApiUrl("academics/classes/global"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGlobalClasses(Array.isArray(res.data) ? res.data : []);
      setSelectedGlobalClassIds(new Set());
      setShowGlobalModal(true);
    } catch (err: any) {
      setGlobalError(err.response?.data?.message || "Failed to load global classes.");
    } finally {
      setGlobalLoading(false);
    }
  }, [token, canImportGlobal]);

  useEffect(() => {
    if (token) {
      fetchClasses();
      fetchTeachers();
    }
  }, [token, fetchClasses, fetchTeachers]);

  const filteredClasses = classes.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.teacher?.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredClasses.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClasses.slice(indexOfFirstItem, indexOfLastItem);

  const classOrder = [
    "Form 5",
    "Form 4",
    "Form 3",
    "Form 2",
    "Form 1",
    "Grade 12",
    "Grade 11",
    "Grade 10",
    "Grade 9",
    "Grade 8",
    "Grade 7",
    "Grade 6",
    "Grade 5",
    "Grade 4",
    "Grade 3",
    "Grade 2",
    "Grade 1",
    "Pregrade",
    "Upper Nursery",
    "Lower Nursery",
    "Toddlers"
  ];
  const classOrderIndex = new Map(classOrder.map((name, index) => [name.toLowerCase(), index]));
  const availableGlobalClasses = globalClasses.filter(
    (cls) => !classes.some((c) => c.name.toLowerCase() === cls.name.toLowerCase())
  );
  const sortedGlobalClasses = [...availableGlobalClasses].sort((a, b) => {
    const aIndex = classOrderIndex.get(a.name.toLowerCase());
    const bIndex = classOrderIndex.get(b.name.toLowerCase());
    if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
    if (aIndex !== undefined) return -1;
    if (bIndex !== undefined) return 1;
    return b.name.localeCompare(a.name);
  });

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openAddModal = () => {
    setEditingClass(null);
    setFormData({ name: "", teacherId: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (cls: ClassType) => {
    if (!canManageClasses) return;
    setEditingClass(cls);
    setFormData({ name: cls.name, teacherId: cls.teacherId || "" });
    setIsModalOpen(true);
  };

  const handleDeleteClass = async (id: string) => {
    if (!canManageClasses) return;
    if (!window.confirm("Are you sure you want to delete this class?")) return;
    try {
      await axios.delete(getApiUrl(`academics/classes/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Class deleted successfully");
      fetchClasses();
      setTimeout(() => setMessage(""), 3000);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Error deleting class");
    }
  };

  const handleToggleGlobalClassSelection = (id: string) => {
    setSelectedGlobalClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAllGlobalClasses = () => {
    if (selectedGlobalClassIds.size === sortedGlobalClasses.length) {
      setSelectedGlobalClassIds(new Set());
      return;
    }
    setSelectedGlobalClassIds(new Set(sortedGlobalClasses.map((cls) => cls.id)));
  };

  const handleImportSelectedGlobalClasses = async () => {
    if (selectedGlobalClassIds.size === 0) {
      setGlobalMessage("Select at least one class to import.");
      return;
    }
    try {
      const ids = Array.from(selectedGlobalClassIds);
      await Promise.all(
        ids.map((id) =>
          axios.post(getApiUrl(`academics/classes/import/${id}`), {}, { headers: { Authorization: `Bearer ${token}` } })
        )
      );
      setGlobalMessage(`Imported ${ids.length} class(es) successfully.`);
      setSelectedGlobalClassIds(new Set());
      setGlobalClasses((prev) => prev.filter((cls) => !ids.includes(cls.id)));
      fetchClasses();
    } catch (err: any) {
      setGlobalMessage(err.response?.data?.message || "Failed to import selected classes.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateClass && !editingClass) {
      setMessage("Only superadmin can create new classes. Please import from global templates.");
      return;
    }
    if (!canManageClasses && editingClass) {
      setMessage("Only admins can update classes.");
      return;
    }
    try {
      const payload = {
        name: formData.name.trim(),
        teacherId: formData.teacherId || undefined,
      };

      if (editingClass) {
        await axios.put(getApiUrl(`academics/classes/${editingClass.id}`), payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(getApiUrl("academics/classes"), payload, { headers: { Authorization: `Bearer ${token}` } });
      }

      fetchClasses();
      setIsModalOpen(false);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to save class");
    }
  };
// Student Assignment Functions
  const openStudentModal = async (cls: ClassType) => {
    if (!canManageClasses) return;
    setSelectedClassForStudents(cls);
    setIsStudentModalOpen(true);
    setSelectedStudentIds(new Set());
    setStudentSearch("");
    setStudentMessage("");
    setStudentError("");
    
    try {
      const [studentsRes, classStudentsRes] = await Promise.all([
        axios.get(getApiUrl("users/students"), { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(getApiUrl(`academics/classes/${cls.id}/students`), { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : studentsRes.data.students || [];
      const classStudentsData = Array.isArray(classStudentsRes.data) ? classStudentsRes.data : [];
      
      setAllStudents(studentsData);
      setClassStudents(classStudentsData);
    } catch (err) {
      console.error("Error fetching students:", err);
    }
  };

  const getAvailableStudents = () => {
    const enrolledIds = new Set(classStudents.map(s => s.id));
    return allStudents.filter(s => !enrolledIds.has(s.id));
  };

  const filteredAvailableStudents = getAvailableStudents().filter(s => 
    s.name.toLowerCase().includes(studentSearch.toLowerCase()) || 
    s.email.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const handleToggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleSelectAllStudents = () => {
    if (selectedStudentIds.size === filteredAvailableStudents.length && 
        filteredAvailableStudents.every(s => selectedStudentIds.has(s.id))) {
      setSelectedStudentIds(new Set());
    } else {
      setSelectedStudentIds(new Set(filteredAvailableStudents.map(s => s.id)));
    }
  };

  const handleBulkAssignStudents = async () => {
    if (!selectedClassForStudents || selectedStudentIds.size === 0) return;
    setIsBulkAssigningStudents(true);
    setStudentError("");
    setStudentMessage("");
    
    try {
      const studentIdsArray = Array.from(selectedStudentIds);
      const res = await axios.post(
        getApiUrl(`academics/classes/${selectedClassForStudents.id}/students/bulk`),
        { studentIds: studentIdsArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setStudentMessage(res.data.message || `Successfully assigned ${res.data.results?.success?.length || 0} student(s)`);
      setSelectedStudentIds(new Set());
      await openStudentModal(selectedClassForStudents); // data
      setTimeout(() => setStudentMessage(""), 5000);
    } catch (err: any) {
      setStudentError(err.response?.data?.message || "Failed to assign students");
      setTimeout(() => setStudentError(""), 5000);
    } finally {
      setIsBulkAssigningStudents(false);
    }
  };

  const handleRemoveStudent = async (studentId: string) => {
    if (!selectedClassForStudents || !window.confirm("Remove student from class?")) return;
    try {
      await axios.delete(
        getApiUrl(`academics/classes/${selectedClassForStudents.id}/students/${studentId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await openStudentModal(selectedClassForStudents); // data
    } catch (err) {
      console.error("Error removing student:", err);
    }
  };
// Subject Assignment Functions
  const openSubjectModal = async (cls: ClassType) => {
    if (!canManageClasses) return;
    setSelectedClassForSubjects(cls);
    setIsSubjectModalOpen(true);
    setSelectedSubjectIds(new Set());
    setSubjectSearch("");
    setSubjectMessage("");
    setSubjectError("");
    
    try {
      const [subjectsRes, classSubjectsRes] = await Promise.all([
        axios.get(getApiUrl("academics/subjects"), { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(getApiUrl(`academics/classes/${cls.id}/subjects`), { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const subjectsData = Array.isArray(subjectsRes.data) ? subjectsRes.data : subjectsRes.data.subjects || [];
      const classSubjectsData = Array.isArray(classSubjectsRes.data) ? classSubjectsRes.data : [];
      
      setAllSubjects(subjectsData);
      setClassSubjects(classSubjectsData);
    } catch (err) {
      console.error("Error fetching subjects:", err);
    }
  };

  const getAvailableSubjects = () => {
    const assignedIds = new Set(classSubjects.map(s => s.id));
    return allSubjects.filter(s => !assignedIds.has(s.id));
  };

  const filteredAvailableSubjects = getAvailableSubjects().filter(s => 
    s.name.toLowerCase().includes(subjectSearch.toLowerCase())
  );

  const handleToggleSubjectSelection = (subjectId: string) => {
    setSelectedSubjectIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subjectId)) {
        newSet.delete(subjectId);
      } else {
        newSet.add(subjectId);
      }
      return newSet;
    });
  };

  const handleSelectAllSubjects = () => {
    if (selectedSubjectIds.size === filteredAvailableSubjects.length && 
        filteredAvailableSubjects.every(s => selectedSubjectIds.has(s.id))) {
      setSelectedSubjectIds(new Set());
    } else {
      setSelectedSubjectIds(new Set(filteredAvailableSubjects.map(s => s.id)));
    }
  };

  const handleBulkAssignSubjects = async () => {
    if (!selectedClassForSubjects || selectedSubjectIds.size === 0) return;
    setIsBulkAssigningSubjects(true);
    setSubjectError("");
    setSubjectMessage("");
    
    try {
      const subjectIdsArray = Array.from(selectedSubjectIds);
      const res = await axios.post(
        getApiUrl(`academics/classes/${selectedClassForSubjects.id}/subjects/bulk`),
        { subjectIds: subjectIdsArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSubjectMessage(res.data.message || `Successfully assigned ${res.data.results?.success?.length || 0} subject(s)`);
      setSelectedSubjectIds(new Set());
      await openSubjectModal(selectedClassForSubjects); // data
      setTimeout(() => setSubjectMessage(""), 5000);
    } catch (err: any) {
      setSubjectError(err.response?.data?.message || "Failed to assign subjects");
      setTimeout(() => setSubjectError(""), 5000);
    } finally {
      setIsBulkAssigningSubjects(false);
    }
  };

  const handleRemoveSubject = async (subjectId: string) => {
    if (!selectedClassForSubjects || !window.confirm("Remove subject from class?")) return;
    try {
      await axios.delete(
        getApiUrl(`academics/classes/${selectedClassForSubjects.id}/subjects/${subjectId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await openSubjectModal(selectedClassForSubjects); // data
    } catch (err) {
      console.error("Error removing subject:", err);
    }
  };

  if (loading && classes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2>Class Management</h2>
              <p className="page-subtitle">Manage all school classes and assigned teachers</p>
            </div>
            {canCreateClass && (
              <button className="utopia-btn-primary flex items-center gap-2" onClick={openAddModal}>
                <Plus size={18} /> Add New Class
              </button>
            )}
          </div>

          {error && (
            <div className="utopia-form-error mb-4">
              {error}
            </div>
          )}

          {teachersError && (
            <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 text-amber-700 text-sm px-4 py-3">
              {teachersError}
            </div>
          )}

          {canImportGlobal && (
            <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-slate-800">Global Class Templates</h3>
                  <p className="text-xs text-slate-500">Copy predefined classes into your school</p>
                </div>
                <button
                  className="utopia-btn-primary"
                  onClick={fetchGlobalClasses}
                  disabled={globalLoading}
                >
                  Import from Global
                </button>
              </div>
            </div>
          )}

          <table className="utopia-table">
            <thead>
              <tr>
                <th>Class Name</th>
                <th>Assigned Teacher</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>
                    {c.teacher ? (
                      <div className="flex flex-col">
                        <span className="font-semibold">{c.teacher.name}</span>
                        <span className="text-xs text-slate-400">{c.teacher.email}</span>
                      </div>
                    ) : (
                      <span className="text-slate-300 italic">Unassigned</span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/classdetails/${c.id}`} className="utopia-btn-icon" title="View Details"><Eye size={16} /></Link>
                      {canManageClasses && (
                        <>
                          <button className="utopia-btn-icon text-blue-500 hover:text-blue-600" onClick={() => openStudentModal(c)} title="Manage Students"><Users size={16} /></button>
                          <button className="utopia-btn-icon text-teal-500 hover:text-teal-600" onClick={() => openSubjectModal(c)} title="Manage Subjects"><Layers size={16} /></button>
                          <button className="utopia-btn-icon" onClick={() => openEditModal(c)} title="Edit Class"><Edit2 size={16} /></button>
                        </>
                      )}
                      {canManageClasses && canDeleteClass && (
                        <button className="utopia-btn-icon text-red-400 hover:text-red-600" onClick={() => handleDeleteClass(c.id)} title="Delete Class"><Trash2 size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {currentItems.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-slate-400 py-8">
                    No classes found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="utopia-pagination">
              <button
                className="utopia-page-btn utopia-page-btn-nav"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              
              {(() => {
                const pages: (number | string)[] = [];
                const maxVisible = 5;
                
                if (totalPages <= maxVisible) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  if (currentPage > 3) pages.push('...');
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  for (let i = start; i <= end; i++) {
                    if (i !== 1 && i !== totalPages) pages.push(i);
                  }
                  if (currentPage < totalPages - 2) pages.push('...');
                  pages.push(totalPages);
                }
                
                return pages.map((p, idx) => {
                  if (p === '...') {
                    return <span key={`ellipsis-${idx}`} className="utopia-pagination-ellipsis">...</span>;
                  }
                  return (
                    <button
                      key={p}
                      className={`utopia-page-btn ${currentPage === p ? 'active' : ''}`}
                      onClick={() => setCurrentPage(p as number)}
                    >
                      {p}
                    </button>
                  );
                });
              })()}
              
              <button
                className="utopia-page-btn utopia-page-btn-nav"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
              
              <div className="utopia-pagination-info">
                <span>
                  Showing <strong>{indexOfFirstItem + 1}</strong> - <strong>{Math.min(indexOfLastItem, filteredClasses.length)}</strong> of <strong>{filteredClasses.length}</strong>
                </span>
              </div>
            </div>
          )}
        </div>
      </main>

      {showGlobalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-slate-800">Global Class Templates</h3>
              <button onClick={() => setShowGlobalModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={22} />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Select classes to import. The list runs from highest to lowest.
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              <button
                className="utopia-btn-secondary"
                onClick={fetchGlobalClasses}
                disabled={globalLoading}
              >
                Refresh
              </button>
              <button
                className="utopia-btn-secondary"
                onClick={handleSelectAllGlobalClasses}
                disabled={globalLoading || sortedGlobalClasses.length === 0}
              >
                {selectedGlobalClassIds.size === sortedGlobalClasses.length ? "Deselect All" : "Select All"}
              </button>
              <button
                className="utopia-btn-primary"
                onClick={handleImportSelectedGlobalClasses}
                disabled={globalLoading || selectedGlobalClassIds.size === 0}
              >
                Import Selected
              </button>
            </div>

            {globalError && (
              <div className="mb-3 rounded-lg border border-rose-100 bg-rose-50 text-rose-700 text-xs px-3 py-2">
                {globalError}
              </div>
            )}

            {globalMessage && (
              <div className="mb-3 rounded-lg border border-teal-100 bg-teal-50 text-teal-700 text-xs px-3 py-2">
                {globalMessage}
              </div>
            )}

            <div className="flex-1 overflow-y-auto pr-2">
              {globalLoading ? (
                <div className="text-sm text-slate-400">Loading global classes...</div>
              ) : sortedGlobalClasses.length === 0 ? (
                <div className="text-sm text-slate-400">No global classes available.</div>
              ) : (
                <div className="space-y-2">
                  {sortedGlobalClasses.map((cls) => (
                    <label key={cls.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 cursor-pointer">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-teal-600"
                          checked={selectedGlobalClassIds.has(cls.id)}
                          onChange={() => handleToggleGlobalClassSelection(cls.id)}
                        />
                        <span className="text-sm font-semibold text-slate-700">{cls.name}</span>
                      </div>
                      <span className="text-[10px] font-black uppercase text-slate-400">Select</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t flex justify-end">
              <button
                onClick={() => setShowGlobalModal(false)}
                className="px-5 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-bold transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="utopia-modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <h3 className="text-lg font-bold">{editingClass ? "Edit Class" : "Add New Class"}</h3>
              <button 
                className="utopia-modal-close-btn" 
                onClick={() => setIsModalOpen(false)}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} onKeyDown={(e) => e.key === 'Escape' && setIsModalOpen(false)}>
              {message && (
                <div className={`${message.includes('Error') ? 'utopia-form-error' : 'utopia-form-success'} mb-4`}>
                  {message}
                </div>
              )}
              <div className="utopia-form-group">
                <label className="utopia-label required">Class Name</label>
                <input
                  type="text"
                  name="name"
                  className="utopia-input"
                  placeholder="Enter class name (e.g. Grade 10-A, Form 3B)"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Assign Teacher</label>
                <select
                  name="teacherId"
                  className="utopia-select"
                  value={formData.teacherId}
                  onChange={handleChange}
                >
                  <option value="">Unassigned</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 mt-4">
                <button 
                  type="button" 
                  className="utopia-btn-secondary flex-1 py-3" 
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary flex-1 py-3">
                  {editingClass ? "Update Class" : "Create Class"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Student Assignment Modal */}
      {isStudentModalOpen && selectedClassForStudents && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsStudentModalOpen(false)}>
          <div className="utopia-modal-content max-w-6xl w-full h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-xl font-bold">Manage Students: {selectedClassForStudents.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{classStudents.length} Students Currently Enrolled</p>
              </div>
              <button className="p-2 hover:bg-slate-50 rounded-full" onClick={() => setIsStudentModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 flex-1 overflow-hidden">
              {/* Left: Enrolled Students */}
              <div className="flex flex-col h-full overflow-hidden border border-slate-100 rounded-2xl bg-white shadow-sm">
                <div className="p-4 border-b border-slate-100 bg-slate-50">
                  <h4 className="font-bold flex items-center gap-2 mb-3">
                    <Users size={18} className="text-blue-500" /> Enrolled Students
                  </h4>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search enrolled..."
                      className="utopia-input pl-10 h-10 text-sm"
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {classStudents.filter(s => s.name.toLowerCase().includes(studentSearch.toLowerCase())).map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-xl hover:border-slate-200 transition-all">
                      <div>
                        <p className="font-semibold text-slate-700 text-sm">{s.name}</p>
                        <p className="text-xs text-slate-400">{s.email}</p>
                      </div>
                      <button className="utopia-btn-icon text-red-400 hover:bg-red-50" onClick={() => handleRemoveStudent(s.id)}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {classStudents.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300">
                      <Users size={48} className="opacity-10 mb-2" />
                      <p className="text-sm italic">No students enrolled</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Available Students */}
              <div className="flex flex-col h-full overflow-hidden border border-slate-100 rounded-2xl bg-white shadow-sm">
                <div className="p-4 border-b border-slate-100 bg-teal-50">
                  <h4 className="font-bold flex items-center gap-2 mb-3">
                    <UserPlus size={18} className="text-teal-500" /> 
                    Available Students
                    {selectedStudentIds.size > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full">
                        {selectedStudentIds.size} selected
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-2 mb-2">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search students..."
                        className="utopia-input pl-10 h-10 text-sm"
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                      />
                    </div>
                    {filteredAvailableStudents.length > 0 && (
                      <button
                        onClick={handleSelectAllStudents}
                        className="px-3 py-2 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1"
                      >
                        {selectedStudentIds.size === filteredAvailableStudents.length ? (
                          <>
                            <CheckSquare size={14} /> Deselect All
                          </>
                        ) : (
                          <>
                            <Square size={14} /> Select All
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {selectedStudentIds.size > 0 && (
                    <button
                      onClick={handleBulkAssignStudents}
                      disabled={isBulkAssigningStudents}
                      className="w-full py-2 px-4 bg-teal-500 text-white text-sm font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isBulkAssigningStudents ? (
                        <>Assigning...</>
                      ) : (
                        <>
                          <UserPlus size={16} /> Assign {selectedStudentIds.size} Student{selectedStudentIds.size > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  )}
                </div>
                {studentMessage && (
                  <div className="mx-4 mt-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-teal-700 text-xs flex items-center gap-2">
                    <CheckCircle2 size={14} /> {studentMessage}
                  </div>
                )}
                {studentError && (
                  <div className="mx-4 mt-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-xs flex items-center gap-2">
                    <AlertCircle size={14} /> {studentError}
                  </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {filteredAvailableStudents.map(s => (
                    <div
                      key={s.id}
                      className={`flex items-center gap-3 p-3 bg-white border rounded-xl transition-all cursor-pointer ${
                        selectedStudentIds.has(s.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-100 hover:border-teal-100'
                      }`}
                      onClick={() => handleToggleStudentSelection(s.id)}
                    >
                      <div className="flex-shrink-0">
                        {selectedStudentIds.has(s.id) ? (
                          <CheckSquare size={20} className="text-teal-500" />
                        ) : (
                          <Square size={20} className="text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-700 text-sm">{s.name}</p>
                        <p className="text-xs text-slate-400">{s.email}</p>
                      </div>
                    </div>
                  ))}
                  {filteredAvailableStudents.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300">
                      <UserPlus size={48} className="opacity-10 mb-2" />
                      <p className="text-sm italic">All students assigned</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Subject Assignment Modal */}
      {isSubjectModalOpen && selectedClassForSubjects && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsSubjectModalOpen(false)}>
          <div className="utopia-modal-content max-w-6xl w-full h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-xl font-bold">Manage Subjects: {selectedClassForSubjects.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{classSubjects.length} Subjects Currently Assigned</p>
              </div>
              <button className="p-2 hover:bg-slate-50 rounded-full" onClick={() => setIsSubjectModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 flex-1 overflow-hidden">
              {/* Left: Assigned Subjects */}
              <div className="flex flex-col h-full overflow-hidden border border-slate-100 rounded-2xl bg-white shadow-sm">
                <div className="p-4 border-b border-slate-100 bg-slate-50">
                  <h4 className="font-bold flex items-center gap-2 mb-3">
                    <BookOpen size={18} className="text-blue-500" /> Assigned Subjects
                  </h4>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search subjects..."
                      className="utopia-input pl-10 h-10 text-sm"
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {classSubjects.filter(s => s.name.toLowerCase().includes(subjectSearch.toLowerCase())).map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-xl hover:border-slate-200 transition-all">
                      <div>
                        <p className="font-semibold text-slate-700 text-sm">{s.name}</p>
                      </div>
                      <button className="utopia-btn-icon text-red-400 hover:bg-red-50" onClick={() => handleRemoveSubject(s.id)}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {classSubjects.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300">
                      <BookOpen size={48} className="opacity-10 mb-2" />
                      <p className="text-sm italic">No subjects assigned</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Available Subjects */}
              <div className="flex flex-col h-full overflow-hidden border border-slate-100 rounded-2xl bg-white shadow-sm">
                <div className="p-4 border-b border-slate-100 bg-teal-50">
                  <h4 className="font-bold flex items-center gap-2 mb-3">
                    <Layers size={18} className="text-teal-500" /> 
                    Available Subjects
                    {selectedSubjectIds.size > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full">
                        {selectedSubjectIds.size} selected
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-2 mb-2">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search subjects..."
                        className="utopia-input pl-10 h-10 text-sm"
                        value={subjectSearch}
                        onChange={(e) => setSubjectSearch(e.target.value)}
                      />
                    </div>
                    {filteredAvailableSubjects.length > 0 && (
                      <button
                        onClick={handleSelectAllSubjects}
                        className="px-3 py-2 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1"
                      >
                        {selectedSubjectIds.size === filteredAvailableSubjects.length ? (
                          <>
                            <CheckSquare size={14} /> Deselect All
                          </>
                        ) : (
                          <>
                            <Square size={14} /> Select All
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {selectedSubjectIds.size > 0 && (
                    <button
                      onClick={handleBulkAssignSubjects}
                      disabled={isBulkAssigningSubjects}
                      className="w-full py-2 px-4 bg-teal-500 text-white text-sm font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isBulkAssigningSubjects ? (
                        <>Assigning...</>
                      ) : (
                        <>
                          <Plus size={16} /> Assign {selectedSubjectIds.size} Subject{selectedSubjectIds.size > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  )}
                </div>
                {subjectMessage && (
                  <div className="mx-4 mt-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-teal-700 text-xs flex items-center gap-2">
                    <CheckCircle2 size={14} /> {subjectMessage}
                  </div>
                )}
                {subjectError && (
                  <div className="mx-4 mt-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-xs flex items-center gap-2">
                    <AlertCircle size={14} /> {subjectError}
                  </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {filteredAvailableSubjects.map(s => (
                    <div
                      key={s.id}
                      className={`flex items-center gap-3 p-3 bg-white border rounded-xl transition-all cursor-pointer ${
                        selectedSubjectIds.has(s.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-100 hover:border-teal-100'
                      }`}
                      onClick={() => handleToggleSubjectSelection(s.id)}
                    >
                      <div className="flex-shrink-0">
                        {selectedSubjectIds.has(s.id) ? (
                          <CheckSquare size={20} className="text-teal-500" />
                        ) : (
                          <Square size={20} className="text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-700 text-sm">{s.name}</p>
                      </div>
                    </div>
                  ))}
                  {filteredAvailableSubjects.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300">
                      <Layers size={48} className="opacity-10 mb-2" />
                      <p className="text-sm italic">All subjects assigned</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllClasses;
